import { Button, Select, Space } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';

export type DiaCobroFiltro = number | null;
export type FacturasVencidasFiltro = 'todos' | 'cero' | 'uno' | 'dosTres' | 'cuatroMas';
export type EstadoCarteraFiltro = 'todos' | 'alDia' | 'leve' | 'moderada' | 'severa';
export type TipoSuscripcionFiltro = 'todos' | 'MENSUAL' | 'PREPAGO';

export interface ClienteMapFilterOption {
  id: number;
  nombre: string;
}

export function tieneFiltros(
  clienteId: number | null,
  diaCobro: DiaCobroFiltro,
  facturasVencidas: FacturasVencidasFiltro,
  estadoCartera: EstadoCarteraFiltro,
  tipoSuscripcion: TipoSuscripcionFiltro,
): boolean {
  return (
    clienteId !== null ||
    diaCobro !== null ||
    facturasVencidas !== 'todos' ||
    estadoCartera !== 'todos' ||
    tipoSuscripcion !== 'todos'
  );
}

interface ClientesMapFilterProps {
  clienteId: number | null;
  diaCobro: DiaCobroFiltro;
  facturasVencidas: FacturasVencidasFiltro;
  estadoCartera: EstadoCarteraFiltro;
  tipoSuscripcion: TipoSuscripcionFiltro;
  clientes: ClienteMapFilterOption[];
  filtrosActivos: boolean;
  onClienteChange: (clienteId: number | null) => void;
  onDiaCobroChange: (diaCobro: DiaCobroFiltro) => void;
  onFacturasVencidasChange: (value: FacturasVencidasFiltro) => void;
  onEstadoCarteraChange: (value: EstadoCarteraFiltro) => void;
  onTipoSuscripcionChange: (value: TipoSuscripcionFiltro) => void;
  onLimpiar: () => void;
}

/** Operational filters for the active-client map. */
export function ClientesMapFilter({
  clienteId,
  diaCobro,
  facturasVencidas,
  estadoCartera,
  tipoSuscripcion,
  clientes,
  filtrosActivos,
  onClienteChange,
  onDiaCobroChange,
  onFacturasVencidasChange,
  onEstadoCarteraChange,
  onTipoSuscripcionChange,
  onLimpiar,
}: ClientesMapFilterProps) {
  const t = useT();

  return (
    <Space wrap size={[8, 8]}>
      <Select<number>
        size="small"
        style={{ minWidth: 180 }}
        value={clienteId ?? undefined}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder={t('dashboard.mapaBuscarCliente')}
        aria-label={t('dashboard.mapaFiltroCliente')}
        onChange={(value) => onClienteChange(value ?? null)}
        options={clientes.map((cliente) => ({
          value: cliente.id,
          label: cliente.nombre || `Cliente #${cliente.id}`,
        }))}
      />
      <Select<number>
        size="small"
        style={{ minWidth: 125 }}
        value={diaCobro ?? undefined}
        allowClear
        placeholder={t('dashboard.mapaTodosDias')}
        aria-label={t('dashboard.mapaFiltroDiaCobro')}
        onChange={(value) => onDiaCobroChange(value ?? null)}
        options={Array.from({ length: 31 }, (_, index) => {
          const dia = index + 1;
          return { value: dia, label: t('dashboard.mapaDiaOption', { dia }) };
        })}
      />
      <Select<FacturasVencidasFiltro>
        size="small"
        style={{ minWidth: 155 }}
        value={facturasVencidas}
        aria-label={t('dashboard.mapaFiltroFacturasVencidas')}
        onChange={onFacturasVencidasChange}
        options={[
          { value: 'todos', label: t('dashboard.mapaTodasVencidas') },
          { value: 'cero', label: t('dashboard.mapaSinVencidas') },
          { value: 'uno', label: t('dashboard.mapaUnaVencida') },
          { value: 'dosTres', label: t('dashboard.mapaDosTresVencidas') },
          { value: 'cuatroMas', label: t('dashboard.mapaCuatroMasVencidas') },
        ]}
      />
      <Select<EstadoCarteraFiltro>
        size="small"
        style={{ minWidth: 135 }}
        value={estadoCartera}
        aria-label={t('dashboard.mapaFiltroCartera')}
        onChange={onEstadoCarteraChange}
        options={[
          { value: 'todos', label: t('dashboard.mapaTodasCarteras') },
          { value: 'alDia', label: t('dashboard.mapaCarteraAlDia') },
          { value: 'leve', label: t('dashboard.mapaCarteraLeve') },
          { value: 'moderada', label: t('dashboard.mapaCarteraModerada') },
          { value: 'severa', label: t('dashboard.mapaCarteraSevera') },
        ]}
      />
      <Select<TipoSuscripcionFiltro>
        size="small"
        style={{ minWidth: 135 }}
        value={tipoSuscripcion}
        aria-label={t('dashboard.mapaFiltroSuscripcion')}
        onChange={onTipoSuscripcionChange}
        options={[
          { value: 'todos', label: t('dashboard.mapaTodosTiposSuscripcion') },
          { value: 'MENSUAL', label: t('dashboard.mapaSuscripcionMensual') },
          { value: 'PREPAGO', label: t('dashboard.mapaSuscripcionPrepago') },
        ]}
      />
      {filtrosActivos && (
        <Button size="small" icon={<ClearOutlined />} onClick={onLimpiar}>
          {t('dashboard.mapaLimpiar')}
        </Button>
      )}
    </Space>
  );
}
